import { useEffect, useMemo, useRef, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import DOMPurify from "dompurify"
import clsx from "clsx"

import { smoketest } from "@/api/smoketest"
import { SmokeLayer } from "@/components/SmokeLayer"
import type { Env, SmokeItem, Train } from "@/types/smoketest"
import type { Releases } from "@/types/sprint-report"

type EnvFilter = "all" | Env
interface EditFields {
  title: string
  note: string
  assignees: string[]
  isTemplate: boolean
}

const ENV_LABEL: Record<Env, string> = { staging: "Staging", prod: "Prod" }
const ENVS: Env[] = ["staging", "prod"]
const SMOKE_PREF_KEY = "smoketest-smoke" // "off" disables the animation for this browser

// Rich-text notes are sanitised (basic formatting only) on save and on render.
const cleanNote = (html: string) =>
  DOMPurify.sanitize(html, { ALLOWED_TAGS: ["b", "strong", "i", "em", "u", "a", "ul", "ol", "li", "p", "br", "span"], ALLOWED_ATTR: ["href", "target", "rel"] })

/** major.minor of a release version ("1.13.0" → "1.13"). */
const mm = (v: string) => v.split(".").slice(0, 2).join(".")
const descSemver = (a: string, b: string) => {
  const [am, an] = a.split(".").map(Number)
  const [bm, bn] = b.split(".").map(Number)
  return bm - am || bn - an
}

function fmtWhen(iso: string | null): string {
  if (!iso) return ""
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
}

const field =
  "w-full rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-ink outline-none focus:border-headerSub focus:ring-2 focus:ring-headerSub/20"
const chipOn = "bg-headerSub/15 text-header ring-headerSub/30"
const chipOff = "bg-white text-slate-500 ring-slate-200 hover:bg-slate-50"

/** Smoke-Test checklist per weekly Release Train, split by Staging / Prod. Any
 *  signed-in member can add, assign, check off, edit and delete; items flagged
 *  "template" carry into the next train. Mirrors the Go-Live tab's shape. */
export function SmokeTesting({ releases }: { releases: Releases | null | undefined }) {
  const qc = useQueryClient()
  const listRef = useRef<HTMLDivElement>(null)
  // Per-viewer smoke-animation toggle, remembered in this browser (default on).
  const [smokeOn, setSmokeOn] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SMOKE_PREF_KEY) !== "off"
    } catch {
      return true
    }
  })
  const toggleSmoke = () =>
    setSmokeOn((v) => {
      const next = !v
      try {
        localStorage.setItem(SMOKE_PREF_KEY, next ? "on" : "off")
      } catch {
        /* ignore */
      }
      return next
    })
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [envFilter, setEnvFilter] = useState<EnvFilter>("all")
  const [newVersion, setNewVersion] = useState("")
  const [creating, setCreating] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const trains = useQuery({ queryKey: ["smoketest", "trains"], queryFn: smoketest.list, staleTime: 60_000 })
  const roster = useQuery({ queryKey: ["smoketest", "people"], queryFn: smoketest.people, staleTime: 60 * 60_000 })

  // Version pulldown for a NEW train: the release versions (major.minor) plus the
  // next one after GA (bump the newest minor), minus versions that already have a
  // train. e.g. 1.14 (next), 1.13, 1.12, 1.11, 1.10.
  const { creatable, nextVersion } = useMemo(() => {
    const known = [...new Set((releases?.versions ?? []).map(mm))].sort(descSemver)
    let next = ""
    if (known.length) {
      const [maj, min] = known[0].split(".").map(Number)
      next = `${maj}.${min + 1}`
    }
    const existing = new Set((trains.data ?? []).map((t) => t.version))
    const all = next && !existing.has(next) ? [next, ...known] : known
    return { creatable: all.filter((v) => !existing.has(v)), nextVersion: next }
  }, [releases, trains.data])

  // Default to the newest train once the list loads.
  useEffect(() => {
    if (selectedId == null && trains.data?.length) setSelectedId(trains.data[0].id)
  }, [trains.data, selectedId])
  // Keep the New-train pulldown pointed at a valid (creatable) version.
  useEffect(() => {
    if (creatable.length && !creatable.includes(newVersion)) setNewVersion(creatable[0])
  }, [creatable, newVersion])

  const train = useQuery({
    queryKey: ["smoketest", "train", selectedId],
    queryFn: () => smoketest.get(selectedId as number),
    enabled: selectedId != null,
    staleTime: 60_000,
  })

  const applyTrain = (updated: Train) => {
    qc.setQueryData(["smoketest", "train", updated.id], updated)
    qc.invalidateQueries({ queryKey: ["smoketest", "trains"] }) // refresh selector counts
  }
  const onErr = (e: unknown) => {
    // A 401 means the access-code session expired — send them back through the
    // site login wall by reloading (the SiteGate re-prompts for the code).
    if ((e as { status?: number }).status === 401) {
      window.location.reload()
      return
    }
    setErr((e as Error).message || "Something went wrong.")
  }

  const mut = useMutation({ mutationFn: (fn: () => Promise<Train>) => fn(), onSuccess: applyTrain, onError: onErr })
  const run = (fn: () => Promise<Train>) => {
    setErr(null)
    mut.mutate(fn)
  }

  // Saving an edit reconciles this item's env membership. Items are per-env:
  // unticking an item's env deletes that entry; ticking the other env adds a
  // separate sibling item; unticking one and ticking the other moves it.
  const saveEdit = async (item: SmokeItem, fields: EditFields, desired: Set<Env>) => {
    setErr(null)
    const other: Env = item.env === "staging" ? "prod" : "staging"
    const tid = train.data!.id
    try {
      let last: Train
      if (desired.has(item.env)) {
        last = await smoketest.patchItem(tid, item.id, fields) // keep + edit fields
        if (desired.has(other)) last = await smoketest.addItem(tid, { envs: [other], ...fields }) // + sibling
      } else if (desired.has(other)) {
        last = await smoketest.patchItem(tid, item.id, { ...fields, env: other }) // move to the other env
      } else {
        last = await smoketest.deleteItem(tid, item.id) // both unticked → delete this entry
      }
      applyTrain(last)
    } catch (e) {
      onErr(e)
    }
  }

  const createTrain = async () => {
    const v = newVersion.trim()
    if (!v) return
    setCreating(true)
    setErr(null)
    try {
      const t = await smoketest.create(v)
      qc.setQueryData(["smoketest", "train", t.id], t)
      await qc.invalidateQueries({ queryKey: ["smoketest", "trains"] })
      setSelectedId(t.id)
      setNewVersion("")
    } catch (e) {
      onErr(e)
    } finally {
      setCreating(false)
    }
  }

  const items = useMemo(() => {
    const all = train.data?.items ?? []
    return envFilter === "all" ? all : all.filter((i) => i.env === envFilter)
  }, [train.data, envFilter])
  const progress = useMemo(() => {
    const total = items.length
    const done = items.filter((i) => i.done).length
    return { total, done, pct: total ? Math.round((100 * done) / total) : 0 }
  }, [items])

  return (
    <div className="space-y-4">
      {/* ---- train selector + create ---- */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Release train</span>
          {trains.data?.length ? (
            <select
              className="rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm font-semibold text-ink"
              value={selectedId ?? ""}
              onChange={(e) => setSelectedId(Number(e.target.value))}
            >
              {trains.data.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.version} — {t.counts.all.done}/{t.counts.all.total} done
                </option>
              ))}
            </select>
          ) : (
            <span className="text-sm text-slate-400">none yet</span>
          )}
        </div>
        {creatable.length > 0 ? (
          <div className="flex items-center gap-1.5">
            <select
              className="rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm"
              value={newVersion}
              onChange={(e) => setNewVersion(e.target.value)}
            >
              {creatable.map((v) => (
                <option key={v} value={v}>
                  {v}
                  {v === nextVersion ? " (next)" : ""}
                </option>
              ))}
            </select>
            <button
              onClick={createTrain}
              disabled={creating || !newVersion}
              className="rounded-lg bg-header px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-ink disabled:opacity-50"
            >
              {creating ? "Creating…" : "New train"}
            </button>
          </div>
        ) : (
          <span className="text-xs text-slate-400">
            {releases?.versions?.length ? "Every release version already has a train." : "No release versions found yet."}
          </span>
        )}
        <p className="basis-full text-[11px] text-slate-400">
          Pick a release version (or <b>{nextVersion || "the next"}</b> for the upcoming GA); creating a train copies
          the checks flagged <b>template</b> from the latest train (unchecked, unassigned).
        </p>
      </div>

      {(err || trains.isError || train.isError) && (
        <p className="rounded-lg bg-red-50 px-3 py-1.5 text-xs text-red-700 ring-1 ring-red-200">
          {err ??
            `Couldn't load the smoke-test data: ${((trains.error ?? train.error) as Error)?.message ?? "try again"}`}
        </p>
      )}

      {trains.isLoading && <p className="p-6 text-sm text-slate-500">Loading…</p>}
      {trains.data && trains.data.length === 0 && (
        <p className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">
          No smoke-test trains yet. Create one for the current release (e.g. <b>1.13</b>) to start the checklist.
        </p>
      )}

      {train.data && (
        <>
          {/* ---- env filter + progress ---- */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="inline-flex overflow-hidden rounded-lg ring-1 ring-slate-200">
              {(["all", "staging", "prod"] as EnvFilter[]).map((e) => (
                <button
                  key={e}
                  onClick={() => setEnvFilter(e)}
                  className={clsx(
                    "px-3 py-1.5 text-xs font-semibold transition",
                    envFilter === e ? "bg-ink text-white" : "bg-white text-slate-500 hover:bg-slate-50",
                  )}
                >
                  {e === "all" ? "All" : ENV_LABEL[e]}
                  <span className="ml-1 opacity-60">{train.data!.counts[e].total}</span>
                </button>
              ))}
            </div>
            <div className="flex items-center gap-3 text-xs text-slate-500">
              <button
                onClick={toggleSmoke}
                title={smokeOn ? "Turn off the smoke animation (saved in this browser)" : "Turn on the smoke animation"}
                aria-pressed={smokeOn}
                className={clsx(
                  "rounded-lg px-2 py-1 text-[11px] font-semibold ring-1 transition",
                  smokeOn ? "bg-headerSub/15 text-header ring-headerSub/30" : "bg-white text-slate-400 ring-slate-200 hover:bg-slate-50",
                )}
              >
                {smokeOn ? "🌫️ Smoke: On" : "Smoke: Off"}
              </button>
              <span className="num font-semibold text-ink">
                {progress.done}/{progress.total} done
              </span>
              <div className="h-1.5 w-28 overflow-hidden rounded-full bg-slate-200">
                <div className="h-full rounded-full bg-win transition-all" style={{ width: `${progress.pct}%` }} />
              </div>
            </div>
          </div>

          {/* ---- add item ---- */}
          <AddItem trainId={train.data.id} roster={roster.data ?? []} onDone={applyTrain} onError={onErr} />

          {/* ---- items ---- */}
          {/* Decorative smoke rises from each item's title (black = pending,
              green = done); the layer overlays the list without catching clicks. */}
          <div ref={listRef} className="relative">
            <SmokeLayer
              containerRef={listRef}
              items={items.map((i) => ({ id: i.id, done: i.done }))}
              burstKey={train.data.id}
              enabled={smokeOn}
            />
            <ul className="space-y-1.5">
              {items.map((it) => (
                <ItemRow
                  key={it.id}
                  item={it}
                  roster={roster.data ?? []}
                  onToggleDone={(done) => run(() => smoketest.patchItem(train.data!.id, it.id, { done }))}
                  onSaveEdit={(fields, desired) => saveEdit(it, fields, desired)}
                  onDelete={() => run(() => smoketest.deleteItem(train.data!.id, it.id))}
                />
              ))}
              {items.length === 0 && (
                <li className="rounded-lg border border-dashed border-slate-200 p-4 text-center text-sm text-slate-400">
                  No checks {envFilter !== "all" ? `for ${ENV_LABEL[envFilter as Env]}` : "yet"}. Add one above.
                </li>
              )}
            </ul>
          </div>
        </>
      )}
    </div>
  )
}

/* ---------------------------------------------------------------- add item */

function AddItem({
  trainId,
  roster,
  onDone,
  onError,
}: {
  trainId: number
  roster: { name: string }[]
  onDone: (t: Train) => void
  onError: (e: unknown) => void
}) {
  const [open, setOpen] = useState(false)
  const [title, setTitle] = useState("")
  const [note, setNote] = useState("")
  const [assignees, setAssignees] = useState<string[]>([])
  const [envs, setEnvs] = useState<Set<Env>>(new Set(["staging"]))
  const [isTemplate, setIsTemplate] = useState(false)
  const [busy, setBusy] = useState(false)

  const reset = () => {
    setTitle("")
    setNote("")
    setAssignees([])
    setEnvs(new Set(["staging"]))
    setIsTemplate(false)
  }
  const submit = async () => {
    if (!title.trim() || envs.size === 0) return
    setBusy(true)
    try {
      // One request creates an item per env atomically (no half-added pair).
      const updated = await smoketest.addItem(trainId, {
        envs: [...envs],
        title: title.trim(),
        note: cleanNote(note),
        assignees,
        isTemplate,
      })
      onDone(updated)
      reset()
      setOpen(false)
    } catch (e) {
      onError(e)
    } finally {
      setBusy(false)
    }
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="rounded-lg border border-dashed border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-500 transition hover:border-header hover:text-header"
      >
        + Add smoke-test check
      </button>
    )
  }
  return (
    <div className="space-y-2 rounded-xl border border-slate-200 bg-slate-50/70 p-3">
      <input className={field} autoFocus placeholder="What to check…" value={title} onChange={(e) => setTitle(e.target.value)} />
      <AssigneePicker roster={roster} value={assignees} onChange={setAssignees} />
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Add to</span>
          {(["staging", "prod"] as Env[]).map((e) => (
            <label key={e} className="flex items-center gap-1 text-xs font-medium text-slate-600">
              <input
                type="checkbox"
                checked={envs.has(e)}
                onChange={(ev) =>
                  setEnvs((prev) => {
                    const n = new Set(prev)
                    if (ev.target.checked) n.add(e)
                    else n.delete(e)
                    return n
                  })
                }
              />
              {ENV_LABEL[e]}
            </label>
          ))}
        </div>
        <label className="flex items-center gap-1 text-xs font-medium text-slate-600">
          <input type="checkbox" checked={isTemplate} onChange={(e) => setIsTemplate(e.target.checked)} /> Template
          <span className="text-slate-400">(carries to next train)</span>
        </label>
      </div>
      <RichText value={note} onChange={setNote} placeholder="Description (optional) — bold, italic, lists" />
      <div className="flex justify-end gap-2">
        <button onClick={() => (reset(), setOpen(false))} className="rounded-lg px-3 py-1.5 text-xs font-semibold text-slate-500 hover:bg-slate-100">
          Cancel
        </button>
        <button
          onClick={submit}
          disabled={busy || !title.trim() || envs.size === 0}
          className="rounded-lg bg-header px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-ink disabled:opacity-50"
        >
          {busy ? "Adding…" : `Add${envs.size === 2 ? " to both" : ""}`}
        </button>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------- assignees */

function AssigneePicker({
  roster,
  value,
  onChange,
}: {
  roster: { name: string }[]
  value: string[]
  onChange: (next: string[]) => void
}) {
  const toggle = (name: string) => {
    const has = value.some((v) => v.toLowerCase() === name.toLowerCase())
    onChange(has ? value.filter((v) => v.toLowerCase() !== name.toLowerCase()) : [...value, name])
  }
  return (
    <div>
      <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-400">Assignees</span>
      <div className="flex flex-wrap gap-1">
        {roster.map((p) => {
          const on = value.some((v) => v.toLowerCase() === p.name.toLowerCase())
          return (
            <button
              key={p.name}
              onClick={() => toggle(p.name)}
              className={clsx("rounded-full px-2 py-0.5 text-[11px] ring-1 transition", on ? chipOn : chipOff)}
            >
              {p.name}
            </button>
          )
        })}
      </div>
    </div>
  )
}

/* ------------------------------------------------------------- rich text */

/** Minimal HTML editor for the description — bold / italic / bullet list. The
 *  value is sanitised (cleanNote) on save and on render, so only safe formatting
 *  survives. External value changes sync in only while unfocused, so typing never
 *  jumps the caret. */
function RichText({
  value,
  onChange,
  placeholder,
}: {
  value: string
  onChange: (html: string) => void
  placeholder?: string
}) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = ref.current
    // Sanitise on the way in too, so nothing unsafe is ever placed in the DOM.
    if (el && document.activeElement !== el && el.innerHTML !== value) el.innerHTML = cleanNote(value)
  }, [value])
  const applyCmd = (cmd: string) => {
    document.execCommand(cmd) // built-in contentEditable formatting (bold/italic/list)
    ref.current?.focus()
    onChange(ref.current?.innerHTML ?? "")
  }
  const tBtn = "rounded px-1.5 py-0.5 text-xs text-slate-500 hover:bg-slate-100"
  const hold = (e: React.MouseEvent) => e.preventDefault() // keep selection when clicking a tool
  return (
    <div className="rounded-lg border border-slate-300 focus-within:border-headerSub focus-within:ring-2 focus-within:ring-headerSub/20">
      <div className="flex gap-0.5 border-b border-slate-200 px-1 py-0.5">
        <button type="button" onMouseDown={hold} onClick={() => applyCmd("bold")} className={clsx(tBtn, "font-bold")}>B</button>
        <button type="button" onMouseDown={hold} onClick={() => applyCmd("italic")} className={clsx(tBtn, "italic")}>I</button>
        <button type="button" onMouseDown={hold} onClick={() => applyCmd("insertUnorderedList")} className={tBtn}>• List</button>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline
        data-placeholder={placeholder}
        onInput={() => onChange(ref.current?.innerHTML ?? "")}
        className="min-h-[52px] px-3 py-1.5 text-sm text-ink outline-none [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5 empty:before:text-slate-400 empty:before:content-[attr(data-placeholder)]"
      />
    </div>
  )
}

/* --------------------------------------------------------------- item row */

const envBadge = (env: Env) =>
  env === "prod"
    ? "bg-priority-p0/10 text-priority-p0 ring-priority-p0/20"
    : "bg-accent-soft text-header ring-headerSub/20"

function initials(name: string): string {
  const p = name.trim().split(/\s+/).filter(Boolean)
  return (p.length >= 2 ? p[0][0] + p[p.length - 1][0] : name.slice(0, 2)).toUpperCase()
}

function ItemRow({
  item,
  roster,
  onToggleDone,
  onSaveEdit,
  onDelete,
}: {
  item: SmokeItem
  roster: { name: string }[]
  onToggleDone: (done: boolean) => void
  onSaveEdit: (fields: EditFields, desiredEnvs: Set<Env>) => void
  onDelete: () => void
}) {
  const [editing, setEditing] = useState(false)
  const [title, setTitle] = useState(item.title)
  const [note, setNote] = useState(item.note)
  const [assignees, setAssignees] = useState<string[]>(item.assignees)
  const [envs, setEnvs] = useState<Set<Env>>(new Set([item.env]))
  const [isTemplate, setIsTemplate] = useState(item.isTemplate)
  const [confirmDel, setConfirmDel] = useState(false)

  if (editing) {
    return (
      <li className="space-y-2 rounded-lg border border-headerSub/40 bg-white p-3">
        <input className={field} value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
        <AssigneePicker roster={roster} value={assignees} onChange={setAssignees} />
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-600">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Env</span>
            {ENVS.map((e) => {
              const on = envs.has(e)
              return (
                <label key={e} className="flex items-center gap-1 font-medium">
                  {/* Each env is its own entry: unticking this item's env deletes it;
                      ticking the other adds a separate item for that env. */}
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() =>
                      setEnvs((prev) => {
                        const n = new Set(prev)
                        if (on) n.delete(e)
                        else n.add(e)
                        return n
                      })
                    }
                  />
                  {ENV_LABEL[e]}
                </label>
              )
            })}
          </div>
          <label className="flex items-center gap-1">
            <input type="checkbox" checked={isTemplate} onChange={(e) => setIsTemplate(e.target.checked)} /> Template
          </label>
        </div>
        {envs.size === 0 && (
          <p className="text-[11px] text-amber-700">No env selected — saving will delete this check.</p>
        )}
        <RichText value={note} onChange={setNote} placeholder="Description (optional) — bold, italic, lists" />
        <div className="flex justify-end gap-2">
          <button onClick={() => setEditing(false)} className="rounded-lg px-3 py-1.5 text-xs font-semibold text-slate-500 hover:bg-slate-100">
            Cancel
          </button>
          <button
            onClick={() => {
              onSaveEdit({ title: title.trim() || item.title, note: cleanNote(note), assignees, isTemplate }, envs)
              setEditing(false)
            }}
            className={clsx(
              "rounded-lg px-3 py-1.5 text-xs font-semibold text-white transition",
              envs.size === 0 ? "bg-red-600 hover:bg-red-700" : "bg-emerald-600 hover:bg-emerald-700",
            )}
          >
            {envs.size === 0 ? "Delete" : "Save"}
          </button>
        </div>
      </li>
    )
  }

  return (
    <li className={clsx("flex items-start gap-3 rounded-lg border p-2.5", item.done ? "border-win/30 bg-win-light/40" : "border-slate-200 bg-white")}>
      <input
        type="checkbox"
        checked={item.done}
        onChange={(e) => onToggleDone(e.target.checked)}
        className="mt-0.5 h-4 w-4 accent-win"
        aria-label={item.done ? "mark not done" : "mark done"}
      />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className={clsx("rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ring-1", envBadge(item.env))}>
            {ENV_LABEL[item.env]}
          </span>
          {item.isTemplate && (
            <span className="rounded px-1.5 py-0.5 text-[10px] font-bold uppercase text-slate-500 ring-1 ring-slate-200" title="Carries into the next train">
              template
            </span>
          )}
          <span data-smoke-id={item.id} className={clsx("text-sm", item.done ? "text-slate-400 line-through" : "text-ink")}>
            {item.title}
          </span>
        </div>
        {item.note && (
          <div
            className="mt-0.5 text-xs text-slate-500 [&_a]:text-header [&_a]:underline [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5"
            // eslint-disable-next-line react/no-danger -- sanitised via cleanNote (DOMPurify)
            dangerouslySetInnerHTML={{ __html: cleanNote(item.note) }}
          />
        )}
        <div className="mt-1 flex flex-wrap items-center gap-1 text-[11px] text-slate-400">
          {item.assignees.map((a) => (
            <span key={a} title={a} className="inline-flex items-center rounded-full bg-accent-soft px-1.5 py-0.5 font-semibold text-header">
              {initials(a)}
            </span>
          ))}
          {item.done && item.doneBy && <span className="ml-1">✓ {item.doneBy} · {fmtWhen(item.doneAt)}</span>}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <button onClick={() => setEditing(true)} className="rounded px-2 py-1 text-[11px] font-semibold text-slate-500 hover:bg-slate-100">
          Edit
        </button>
        {confirmDel ? (
          <button onClick={onDelete} onMouseLeave={() => setConfirmDel(false)} className="rounded px-2 py-1 text-[11px] font-semibold text-white bg-red-600 hover:bg-red-700">
            Confirm
          </button>
        ) : (
          <button onClick={() => setConfirmDel(true)} className="rounded px-2 py-1 text-[11px] font-semibold text-slate-400 hover:bg-red-50 hover:text-red-600">
            Delete
          </button>
        )}
      </div>
    </li>
  )
}
